import type { FloodDamageData, FloodDamageRasterRef, FloodDamageRow } from '../../../types/floodDamage';
import floodDamageRastersJson from '../../../assets/floodDamageRasters.json';

/** Default flood event shown in the Loss & Damage panel. */
export const DEFAULT_FLOOD_DAMAGE_EVENT = 'Habagat';

export const FLOOD_DAMAGE_COLUMN_HELP = {
  total: {
    label: 'Total',
    title: 'Total damage (₱)',
    text: 'Best estimate of agricultural loss and damage for this municipality, in Philippine pesos (₱). Amounts of ₱1 million or more are shown as ₱M.',
  },
  ciLow: {
    label: 'CI low',
    title: 'CI low — confidence interval lower (₱)',
    text: 'Lower bound of the confidence interval in Philippine pesos (₱). Amounts of ₱1 million or more are shown as ₱M.',
  },
  ciHigh: {
    label: 'CI high',
    title: 'CI high — confidence interval upper (₱)',
    text: 'Upper bound of the confidence interval in Philippine pesos (₱). Amounts of ₱1 million or more are shown as ₱M.',
  },
} as const;

/** Map legend for the RapidDamage baseline-yield band (uint8 1–6). */
export const FLOOD_DAMAGE_RASTER_LEGEND = {
  title: 'Baseline yield',
  colors: ['#ffffb2', '#fecc5c', '#fd8d3c', '#f03b20', '#bd0026'],
  labels: ['Low', 'High'],
  min: 1,
  max: 6,
  description:
    'Relative baseline rice yield from the RapidDamage GeoTIFF (scaled 1–6). Transparent areas are nodata (no crop / no estimate).',
} as const;

/** Exact peso string for tooltips, e.g. "₱32,518,241". */
export function formatFloodDamagePhpExact(value: string | undefined | null): string {
  if (isInvalidFloodDamageValue(value)) return '—';
  const n = parseDamageNumber(String(value));
  if (!Number.isFinite(n) || n === Number.NEGATIVE_INFINITY) {
    const trimmed = String(value).trim();
    return trimmed.startsWith('₱') ? trimmed : `₱${trimmed}`;
  }
  return `₱${n.toLocaleString('en-PH', { maximumFractionDigits: 0 })}`;
}
 
/** Display a damage figure as Philippine pesos. Values ≥ 1 million use ₱M (e.g. "₱32.5M"). */
export function formatFloodDamagePhp(value: string | undefined | null): string {
  if (isInvalidFloodDamageValue(value)) return '—';
  const n = parseDamageNumber(String(value));
  if (!Number.isFinite(n) || n === Number.NEGATIVE_INFINITY) {
    return formatFloodDamagePhpExact(value);
  }

  if (Math.abs(n) >= 1_000_000) {
    const millions = n / 1_000_000;
    const digits = Math.abs(millions) >= 10 ? 1 : 2;
    const compact = millions.toFixed(digits).replace(/\.?0+$/, '');
    return `₱${compact}M`;
  }

  return formatFloodDamagePhpExact(value);
}

export interface FloodDamageEventOption {
  key: string;
  label: string;
  dateLabel: string;
  count: number;
}

export function isInvalidFloodDamageValue(value: string | undefined | null): boolean {
  if (value == null) return true;
  const normalized = String(value).trim().toUpperCase();
  return normalized === 'NAN' || normalized === 'NA' || normalized === '';
}

export function isValidFloodDamageRow(row: FloodDamageRow): boolean {
  return !(
    isInvalidFloodDamageValue(row.TotalDamage) &&
    isInvalidFloodDamageValue(row.CI_Lower) &&
    isInvalidFloodDamageValue(row.CI_Upper)
  );
}

/** "Habagat (2025-07-21)" → "Habagat" */
export function floodEventKey(event: string | undefined | null): string {
  if (!event) return '';
  return event.replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/, '').trim() || event.trim();
}

/** "Habagat (2025-07-21)" → "21 Jul 2025" */
export function floodEventDateLabel(event: string | undefined | null): string {
  if (!event) return '';
  const match = event.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const date = new Date(`${match[1]}-${match[2]}-${match[3]}T00:00:00`);
  if (Number.isNaN(date.getTime())) return match[0];
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function slug(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\w.-]/g, '')
    .replace(/_+/g, '_');
}

function parseDamageNumber(value: string | undefined): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const n = Number(String(value).replace(/[₱,\s]/g, ''));
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

function placeId(eventKey: string, municipality: string): string {
  return `ld_${slug(eventKey)}_${slug(municipality)}`;
}

function normPlaceName(value: string | undefined | null): string {
  return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

const FLOOD_DAMAGE_RASTERS = floodDamageRastersJson as FloodDamageRasterRef[];

const rastersByMunicipality = (() => {
  const map = new Map<string, FloodDamageRasterRef[]>();
  for (const raster of FLOOD_DAMAGE_RASTERS) {
    const key = normPlaceName(raster.municipality);
    const list = map.get(key);
    if (list) list.push(raster);
    else map.set(key, [raster]);
  }
  return map;
})();

/** Pick the RapidDamage GeoTIFF for a municipality, using the source file/province when names collide. */
export function findFloodDamageRaster(
  municipality: string,
  hintName?: string
): FloodDamageRasterRef | undefined {
  const hits = rastersByMunicipality.get(normPlaceName(municipality)) || [];
  if (hits.length === 0) return undefined;
  if (hits.length === 1) return hits[0];
  const hint = normPlaceName(hintName);
  if (hint) {
    const byProvince = hits.find((raster) => normPlaceName(raster.province) === hint);
    if (byProvince) return byProvince;
  }
  return hits[0];
}

/**
 * Flatten grouped flood-damage files into one list item per municipality per event.
 * When the same municipality appears in multiple source files for one event, prefer
 * the file whose label matches the municipality; otherwise keep the higher damage.
 */
export function flattenFloodDamagePlaces(source: FloodDamageData[]): FloodDamageData[] {
  const chosen = new Map<string, { place: FloodDamageData; parentName: string }>();

  for (const entry of source) {
    for (const row of entry.data || []) {
      if (!isValidFloodDamageRow(row)) continue;
      const municipality = (row.Municipality || '').trim();
      if (!municipality) continue;
      const event = (row.Event || '').trim();
      const key = floodEventKey(event);
      const id = placeId(key || 'event', municipality);
      const lat = typeof row.lat === 'number' ? row.lat : entry.lat;
      const lon = typeof row.lon === 'number' ? row.lon : entry.lon;
      const place: FloodDamageData = {
        id,
        municipalityName: municipality,
        lat,
        lon,
        raster: findFloodDamageRaster(municipality, entry.municipalityName),
        data: [
          {
            Municipality: municipality,
            TotalDamage: row.TotalDamage,
            CI_Lower: row.CI_Lower,
            CI_Upper: row.CI_Upper,
            Event: event || undefined,
            lat,
            lon,
          },
        ],
      };

      const existing = chosen.get(id);
      if (!existing) {
        chosen.set(id, { place, parentName: entry.municipalityName });
        continue;
      }

      const parentMatches = entry.municipalityName.trim().toLowerCase() === municipality.toLowerCase();
      const existingParentMatches =
        existing.parentName.trim().toLowerCase() === municipality.toLowerCase();
      if (parentMatches && !existingParentMatches) {
        chosen.set(id, { place, parentName: entry.municipalityName });
        continue;
      }
      if (!parentMatches && existingParentMatches) continue;

      const nextDamage = parseDamageNumber(row.TotalDamage);
      const prevDamage = parseDamageNumber(existing.place.data[0]?.TotalDamage);
      if (nextDamage > prevDamage) {
        chosen.set(id, { place, parentName: entry.municipalityName });
      }
    }
  }

  return [...chosen.values()]
    .map(({ place }) => place)
    .sort((a, b) => a.municipalityName.localeCompare(b.municipalityName));
}

export function listFloodDamageEvents(places: FloodDamageData[]): FloodDamageEventOption[] {
  const counts = new Map<string, { event: string; count: number }>();
  for (const place of places) {
    const event = place.data[0]?.Event || '';
    const key = floodEventKey(event);
    if (!key) continue;
    const current = counts.get(key);
    if (current) current.count += 1;
    else counts.set(key, { event, count: 1 });
  }

  const options = [...counts.entries()].map(([key, { event, count }]) => ({
    key,
    label: key,
    dateLabel: floodEventDateLabel(event),
    count,
  }));

  options.sort((a, b) => {
    if (a.key === DEFAULT_FLOOD_DAMAGE_EVENT) return -1;
    if (b.key === DEFAULT_FLOOD_DAMAGE_EVENT) return 1;
    return a.label.localeCompare(b.label);
  });

  return options;
}

export function resolveFloodDamageEventKey(
  events: FloodDamageEventOption[],
  preferred = DEFAULT_FLOOD_DAMAGE_EVENT
): string {
  if (events.some((event) => event.key === preferred)) return preferred;
  return events[0]?.key ?? preferred;
}

export function placesForFloodEvent(places: FloodDamageData[], eventKey: string): FloodDamageData[] {
  return places.filter((place) => floodEventKey(place.data[0]?.Event) === eventKey);
}
